import { isTRPCClientError, trpcErrorEventEmitter } from '#composables/useTrpc';
import { config } from '#config/configuration';
import { useImpersonateToken } from '#domain/core/admin/composables/useImpersonateToken';
import { useAnalytics } from '#domain/core/analytics/composables/useAnalytics';
import { defineStore } from 'pinia';
import { changeLanguage } from '../i18n';
import { useUserStore } from './user.store';

interface UserAuthFieldsType {
  userId?: string;
  lastSignInTime?: string;
  getAccessToken?: () => Promise<string | null>;
  hasAuthError?: boolean;
}
interface AuthStoreStateType {
  isInitialized: boolean;
  auth: UserAuthFieldsType | null | undefined;
}
export const useAuthStore = defineStore('auth', {
  state: (): AuthStoreStateType => ({
    isInitialized: false,
    auth: undefined
  }),
  getters: {
    isAuthenticated: (state) => !!state.auth?.userId,
    userId: (state) => state.auth?.userId,
    hasAuthError: (state) => state.auth?.hasAuthError ?? false
  },
  actions: {
    async initAuth() {
      const userStore = useUserStore();

      trpcErrorEventEmitter.addEventListener('unauthorized', () => {
        const routes = this.router.getRoutes();
        const currentRoute = routes.find((route) => route.path === window.location.pathname);

        if (currentRoute?.meta.isUnauthenticated) {
          return;
        }

        sessionStorage.setItem('afterLoginRedirectPath', window.location.pathname + window.location.search);

        window.location.assign(config.login.apiLoginPath ?? '/error');
      });

      try {
        const authenticatedUser = await userStore.fetchAuthenticatedUser();

        const afterLoginRedirectPath = sessionStorage.getItem('afterLoginRedirectPath');
        sessionStorage.removeItem('afterLoginRedirectPath');

        if (afterLoginRedirectPath) {
          window.location.assign(afterLoginRedirectPath);
        }

        this.auth = {
          userId: authenticatedUser.id,
          lastSignInTime: undefined,
          getAccessToken: () => Promise.resolve(null),
          hasAuthError: false
        };

        if (authenticatedUser.appScope?.lang && !authenticatedUser.isImpersonating) {
          changeLanguage(authenticatedUser.appScope.lang);
        }
      } catch (error) {
        const hasAuthError = !(isTRPCClientError(error) && error.data?.httpStatus === 401);
        this.auth = {
          hasAuthError
        };
        // let the trpc middleware trigger the unauthorized event and redirect to the login page if needed
      } finally {
        this.isInitialized = true;
      }
    },
    async logout() {
      const { trackEvent } = useAnalytics();
      trackEvent({ category: 'account', event: 'logout', action: 'logout' }, this.router.currentRoute.value);

      const impersonateToken = useImpersonateToken();
      if (impersonateToken.value) {
        impersonateToken.value = null;
      }

      window.location.assign(config.login.apiLogoutPath ?? '/error');
    }
  }
});
